import React, { useMemo } from "react";
import { Link } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import Badge from "@components/Badge/Badge";
import type { PlannedSplit } from "@services/drybulkCargoOperationsApi";
import { useTranslation } from "react-i18next";
import { DRYBULK_CARGO_OPS_LANG_BASE } from "../constants";
import { percent } from "../utils/closeOutFormat";
import OperationDataGrid from "./OperationDataGrid";
import RowActionsMenu, { type RowAction } from "./RowActionsMenu";

export interface PartyGridStatus {
  label: string;
  prefix: string;
  badge?: "success" | "error" | "neutral";
}

export interface PartyGridRow {
  code: string | null;
  planned: number | null;
  splits: PlannedSplit[] | null;
  status?: string;
  statusCell?: PartyGridStatus;
  actual?: number | null;
  intermediate?: number | null;
  heldIntermediate?: number | null;
  adjusted?: number | null;
  tolerance?: number | null;
  actions?: RowAction[];
}

const text = (value: string | number | null | undefined) =>
  value === null || value === undefined ? "-" : String(value);

const PartyGrid: React.FC<{
  langKey: "shippers-grid" | "stock-piles-grid";
  testId: string;
  rows: PartyGridRow[];
  withStatus?: boolean;
  withIntermediate?: boolean;
  withAdjusted?: boolean;
  withTolerance?: boolean;
  onOpen: (code: string) => void;
}> = ({
  langKey,
  testId,
  rows,
  withStatus = false,
  withIntermediate = false,
  withAdjusted = false,
  withTolerance = false,
  onOpen,
}) => {
  const { t } = useTranslation();
  const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.sequences.${langKey}`;
  const withActions = rows.some((row) => (row.actions ?? []).length > 0);

  const columns = useMemo<GridColDef[]>(
    () => [
      { field: "rowNo", headerName: t(`${L}.id`), flex: 0.5, minWidth: 56 },
      {
        field: "companyName",
        headerName: t(`${L}.company-name`),
        flex: 1.4,
        minWidth: 110,
        renderCell: (params: GridRenderCellParams) =>
          typeof params.row.rawCode === "string" ? (
            <Link
              component="button"
              type="button"
              underline="hover"
              data-testid={`${testId}-link-${params.row.id}`}
              onClick={() => onOpen(params.row.rawCode)}
              sx={{
                fontWeight: 600,
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {params.row.companyName}
            </Link>
          ) : (
            params.row.companyName
          ),
      },
      { field: "cargoCode", headerName: t(`${L}.cargo-code`), flex: 1, minWidth: 80 },
      { field: "sizes", headerName: t(`${L}.grade-sizes`), flex: 1, minWidth: 70 },
      { field: "planned", headerName: t(`${L}.planned`), flex: 1, minWidth: 80 },
      ...(withIntermediate
        ? [
            {
              field: "intermediate",
              headerName: t(`${L}.intermediate`),
              flex: 1,
              minWidth: 90,
              renderCell: (params: GridRenderCellParams) =>
                params.row.intermediateHeld ? (
                  <i
                    data-testid={`${testId}-held-${params.row.id}`}
                    aria-label={`${params.value} (${t(`${L}.intermediate-not-sent`)})`}
                  >
                    {params.value}
                  </i>
                ) : (
                  params.value
                ),
            },
          ]
        : []),
      { field: "actual", headerName: t(`${L}.actual`), flex: 1, minWidth: 70 },
      ...(withAdjusted
        ? [{ field: "adjusted", headerName: t(`${L}.adjusted`), flex: 1, minWidth: 90 }]
        : []),
      ...(withTolerance
        ? [{ field: "tolerance", headerName: t(`${L}.tolerance`), flex: 1, minWidth: 70 }]
        : []),
      ...(withStatus
        ? [
            {
              field: "partyStatus",
              headerName: t(`${L}.status`),
              flex: 1,
              minWidth: 100,
              renderCell: (params: GridRenderCellParams) => {
                const cell: PartyGridStatus | undefined = params.row.statusCell;
                if (!cell) {
                  return params.value;
                }
                return (
                  <span
                    aria-label={`${cell.prefix}: ${cell.label}`}
                    data-testid={`${testId}-status-${params.row.id}`}
                  >
                    <span className="sr-only">{cell.prefix}: </span>
                    {cell.badge ? <Badge type={cell.badge} label={cell.label} /> : cell.label}
                  </span>
                );
              },
            },
          ]
        : []),
      ...(withActions
        ? [
            {
              field: "partyActions",
              headerName: t("common.action-column"),
              width: 64,
              align: "center" as const,
              headerAlign: "center" as const,
              renderCell: (params: GridRenderCellParams) =>
                (params.row.actions as RowAction[]).length > 0 ? (
                  <RowActionsMenu
                    testId={`${testId}-menu-${params.row.id}`}
                    actions={params.row.actions}
                  />
                ) : null,
            },
          ]
        : []),
    ],
    [t, L, withStatus, withIntermediate, withAdjusted, withTolerance, withActions, testId, onOpen],
  );

  const gridRows = useMemo(() => {
    const shownFirst = new Set<string>();
    const flat = rows.flatMap((row) => {
      const splits = row.splits ?? [];
      const firstOfCode = () => {
        const key = row.code ?? "";
        if (shownFirst.has(key)) {
          return false;
        }
        shownFirst.add(key);
        return true;
      };
      const figures = () => {
        if (!firstOfCode()) {
          return {
            actual: "-",
            intermediate: "-",
            intermediateHeld: false,
            adjusted: "-",
            tolerance: "-",
            actions: [] as RowAction[],
          };
        }
        const served = row.intermediate !== null && row.intermediate !== undefined;
        const held = !served && row.heldIntermediate !== null && row.heldIntermediate !== undefined;
        return {
          actual: text(row.actual),
          intermediate: served ? text(row.intermediate) : held ? text(row.heldIntermediate) : "-",
          intermediateHeld: held,
          adjusted: text(row.adjusted),
          tolerance: percent(row.tolerance),
          actions: row.actions ?? [],
        };
      };
      const common = { rawCode: row.code, status: row.status ?? "-", statusCell: row.statusCell };
      return splits.length === 0
        ? [
            {
              ...common,
              cargoCode: "-",
              sizes: "-",
              planned: text(row.planned),
              ...figures(),
            },
          ]
        : splits.map((split) => ({
            ...common,
            cargoCode: text(split.cargoCode),
            sizes: text(split.gradeSizeCode),
            planned: text(split.planned),
            ...figures(),
          }));
    });
    return flat.map((row, index) => ({
      id: String(index + 1),
      rowNo: index + 1,
      companyName: text(row.rawCode),
      rawCode: row.rawCode,
      cargoCode: row.cargoCode,
      sizes: row.sizes,
      planned: row.planned,
      intermediate: row.intermediate,
      intermediateHeld: row.intermediateHeld,
      actual: row.actual,
      adjusted: row.adjusted,
      tolerance: row.tolerance,
      partyStatus: row.status,
      statusCell: row.statusCell,
      actions: row.actions,
    }));
  }, [rows]);

  return (
    <OperationDataGrid
      title={t(`${L}.title`)}
      testId={testId}
      columns={columns}
      plainFields={["companyName", "partyActions"]}
      rows={gridRows}
      emptyMessage={t(`${L}.empty`)}
    />
  );
};

export default PartyGrid;
